// The deity name plate used everywhere a DMT/deity is titled — a gilded pill
// with the tool's icon, its name, and its ascension tier (DEMI / TYTN / OLMPN).
// One component so every deity title on every screen reads identically.
import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { TOOL_NAMES } from '../domain/olmps';
import { DISPLAY_FONT, makeThemedStyles, useTheme } from '../v10/theme';
import { GlassCard, Medallion } from '../v10/ui';
import { portraitFor } from '../v10/roster';
import { iconForTool } from './toolIcons';
import { tierOf } from './ascension';
import type { DmtToolId } from './spectrum-tools';

/** Tier id → the badge the roster uses (ascension.ts's DEMI / TYTN / OLMPN). */
const TIER_LABEL: Record<string, string> = {
  mortal: 'DEMI',
  titan: 'TYTN',
  olympian: 'OLMPN',
};

export function tierLabelFor(toolId: string): string | null {
  const t = tierOf(toolId as DmtToolId);
  return t ? TIER_LABEL[t.tier] ?? null : null;
}

export function DeityPlacard({
  toolId,
  /** Optional trailing note — a score, a count. Kept small and after the tier. */
  note,
  onPress,
  size = 'md',
}: {
  toolId: string;
  note?: string | null;
  onPress?: () => void;
  size?: 'sm' | 'md';
}) {
  const { t } = useTheme();
  const styles = useStyles();
  const Icon = iconForTool(toolId);
  const tier = tierLabelFor(toolId);
  const name = TOOL_NAMES[toolId] ?? toolId.toUpperCase();
  const small = size === 'sm';
  const hasPortrait = !!portraitFor(toolId);

  const plate = (
    <GlassCard style={[styles.plate, small && styles.plateSm]}>
      {hasPortrait ? (
        <Medallion id={toolId} size={small ? 20 : 26} />
      ) : (
        <Icon size={small ? 13 : 16} color={t.gold} strokeWidth={2.4} />
      )}
      <Text style={[styles.name, small && styles.nameSm]}>{name.toUpperCase()}</Text>
      {tier && <Text style={[styles.tier, small && styles.tierSm]}>- {tier}</Text>}
      {note ? <Text style={[styles.note, small && styles.tierSm]}>{note}</Text> : null}
    </GlassCard>
  );

  if (!onPress) return <View style={styles.row}>{plate}</View>;
  return (
    <Pressable style={styles.row} onPress={onPress}>
      {plate}
    </Pressable>
  );
}

const useStyles = makeThemedStyles(({ t }) =>
  StyleSheet.create({
    row: { alignItems: 'center' },
    plate: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      alignSelf: 'center',
      paddingHorizontal: 14,
      paddingVertical: 6,
      borderRadius: 999,
    },
    plateSm: { paddingHorizontal: 10, paddingVertical: 4, gap: 6 },
    name: {
      fontSize: 15,
      letterSpacing: 2,
      color: t.ivory,
      fontFamily: DISPLAY_FONT,
    },
    nameSm: { fontSize: 12, letterSpacing: 1.5 },
    tier: { fontSize: 10, fontWeight: '800', letterSpacing: 0.8, color: t.gold },
    tierSm: { fontSize: 9 },
    note: { fontSize: 10, fontWeight: '800', color: t.ivorySoft },
  }),
);

export default DeityPlacard;
